<template>
    <section class="faq-section">
        <div class="container">
            <div class="section-header">
                <h2 class="section-title">{{ title }}</h2>
            </div>

            <div class="faq-list">
                <div
                    v-for="(faq, index) in faqs"
                    :key="index"
                    class="faq-item"
                    :class="{ active: activeFaqIndex === index }"
                >
                    <div class="faq-question" @click="toggleFaq(index)">
                        <!-- <span class="faq-icon">></span> -->
                        <span class="faq-text">{{ faq.question }}</span>
                    </div>
                    <div class="faq-answer">
                        <p>{{ faq.answer }}</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'

interface FAQ {
    question: string
    answer: string
}

interface Props {
    title: string
    faqs: FAQ[]
}

defineProps<Props>()

const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}
</script>

<style lang="less" scoped>
.faq-section {
    padding: 6rem 0;
    background-color: #0d0e12;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
}

.section-title {
    font-size: 3.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
    line-height: 1.2;
}

.faq-list {
    max-width: 800px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
}

.faq-item {
    padding: 2rem 0;
    background-color: #22242d;
    border-radius: 16px;

    &:last-child {
        border-bottom: none;
    }
}

.faq-question {
    display: flex;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
    transition: color 0.2s ease;
    padding-left: 2rem;
    color: #f5f2f0;

    &:hover {
        color: #60a5fa;
    }
}

.faq-text {
    font-size: 1.125rem;
    font-weight: 600;
    color: white;
}

.faq-answer {
    padding: 0 2rem;
    opacity: 0;
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease;
    color: #adadad;

    .faq-item.active & {
        opacity: 1;
        max-height: 500px;
        padding-top: 1rem;
    }

    p {
        color: rgba(255, 255, 255, 0.8);
        line-height: 1.6;
        margin: 0;
    }
}

@media (max-width: 768px) {
    .faq-section {
        padding: 4rem 0;
    }

    .section-title {
        font-size: 2rem;
        padding: 0 1rem;
    }

    .section-header {
        margin-bottom: 2rem;
        padding: 0 1rem;
    }

    .faq-list {
        padding: 0 1rem;
    }

    .faq-item {
        padding: 1.5rem 0;
    }

    .faq-question {
        padding: 0 1.5rem;
        font-size: 1rem;
    }

    .faq-text {
        font-size: 1rem;
    }

    .faq-answer {
        padding: 0 1.5rem;
        font-size: 0.875rem;
    }
}

@media (max-width: 480px) {
    .faq-section {
        padding: 3rem 0;
    }

    .section-title {
        font-size: 1.5rem;
    }

    .section-header {
        margin-bottom: 1.5rem;
    }

    .faq-item {
        padding: 1rem 0;
    }

    .faq-question {
        padding: 0 1rem;
        font-size: 0.9rem;
    }

    .faq-text {
        font-size: 0.9rem;
    }

    .faq-answer {
        padding: 0 1rem;
        font-size: 0.8rem;
    }
}
</style>

